// Composer Studio — 표 열/행 경계 드래그 리사이즈의 순수 재배분 로직.
//
// 캔버스에서 셀 세로/가로 경계를 끌어 colWidths/rowHeights를 조정한다(엑셀/한글식). DOM 측정과
// 드래그 UX는 TableEdgeResizeOverlay가 담당하고, 여기서는 "기존 폭/높이 배열 + 경계 인덱스 +
// 델타 → 새 배열"의 순수 계산만 분리해 단위 테스트한다.
//
// 정책(열):
//   - 경계 왼쪽 열 i의 colWidths[i]만 조정한다(다른 열 불변 = 엑셀식으로 표 전체 폭이 따라 변함.
//     hug 표는 폭 합이 변하고, 'auto' 포함 fill 표는 auto 열이 나머지를 흡수).
//   - i열이 구체 number면 그 값을 기준으로, 아니면('auto'/'%'/문자열) 드래그 시점 실측 px(measuredPx)로
//     구체값 전환 후 조정한다(auto 열의 오른쪽 경계 드래그 = 실측→구체 전환).
//   - 최소 폭 가드 MIN_EDGE_PX.
//   - 외곽(가장자리) 경계 지원(Word/Docs식): 오른쪽 외곽 = 마지막 열 폭 조정(내부 규칙의 자연 확장,
//     바깥으로 끌면 grow). 왼쪽 외곽 = 첫 열 폭 조정 — 바깥(왼쪽)으로 끌면 grow라 델타 부호를 반전한다
//     (reverse=true). 위/아래 외곽 행도 동일(위 외곽=첫 행, reverse).
// 행(rowHeights)도 동일 규칙.

/** 열/행 최소 크기(px) 가드. */
export const MIN_EDGE_PX = 12;

/**
 * colWidths가 colCount개 모두 구체값(number/px/%/문자열, 'auto'·미지정 제외)인지 — RenderTemplate의
 * colWidthsAllConcrete와 동일 기준(hug 표 판정의 핵심). 전 칼럼이 구체값이면 표 폭이 폭 합으로
 * 결정되어(auto degrade 없이) 안정적으로 hug한다.
 */
export function tableColWidthsAllConcrete(
  colWidths: (number | string)[] | undefined,
  colCount: number,
): boolean {
  if ((colWidths?.length ?? 0) < colCount) return false;
  return colWidths!
    .slice(0, colCount)
    .every((w) => w !== undefined && w !== null && w !== 'auto');
}

/**
 * 가로 외곽(왼쪽/오른쪽) 경계 리사이즈를 활성화할지 — hug 표만 허용한다.
 * 조건: 전 칼럼 구체값 colWidths + 표 width 미지정/'auto'(부모 폭에 매이지 않아 폭 합이 곧 표 폭).
 * fill(width='fill'·number·'%')이나 auto 열이 섞인 표는 가로가 부모/내용에 매여 외곽 드래그가
 * "바깥으로 grow"로 이어지지 않으므로 비활성(세로 외곽은 별개로 항상 활성).
 */
export function horizontalOuterResizeEnabled(
  colWidths: (number | string)[] | undefined,
  colCount: number,
  width: number | string | undefined,
): boolean {
  return (
    tableColWidthsAllConcrete(colWidths, colCount) &&
    (width === undefined || width === 'auto')
  );
}

/** 부분/문자열 혼재 배열을 length 길이로 정규화(미지정=undefined). */
function normalize(
  arr: (number | string)[] | undefined,
  len: number,
): (number | string | undefined)[] {
  return Array.from({ length: len }, (_, i) => arr?.[i]);
}

/**
 * 대상 열 targetCol의 너비를 deltaPx만큼 조정한 새 colWidths(길이=colCount)를 만든다.
 * 반환은 setColWidths에 그대로 넘길 수 있는 (number|string)[]로, 미지정 칸은 'auto'로 채운다
 * (속성 패널의 표시/저장 규칙과 동일 — 기존 undefined는 렌더상 auto와 동치라 회귀 0).
 *
 * 내부/오른쪽 외곽 경계는 경계 왼쪽 열(=targetCol)을 조정(오른쪽 드래그=grow). 왼쪽 외곽 경계는
 * 첫 열(targetCol=0)을 조정하되 바깥(왼쪽)으로 끌면 grow라 reverse=true로 델타 부호를 반전한다.
 */
export function resizeColumnWidths(
  colWidths: (number | string)[] | undefined,
  colCount: number,
  targetCol: number,
  deltaPx: number,
  measuredPx: number[],
  minPx: number = MIN_EDGE_PX,
  reverse: boolean = false,
): (number | string)[] {
  const norm = normalize(colWidths, colCount);
  const cur = norm[targetCol];
  // number면 저장값 기준, 아니면 실측 px로 구체 전환.
  const base = typeof cur === 'number' ? cur : (measuredPx[targetCol] ?? minPx);
  const effDelta = reverse ? -deltaPx : deltaPx;
  const next = Math.max(minPx, Math.round(base + effDelta));
  const out: (number | string)[] = norm.map((w) => (w === undefined ? 'auto' : w));
  out[targetCol] = next;
  return out;
}

/**
 * 대상 행 targetRow의 높이를 deltaPx만큼 조정한 새 rowHeights(길이=rowCount)를 만든다.
 * 규칙은 resizeColumnWidths와 동일(구체 number 기준 or 실측 전환 + min 가드 + reverse 부호 반전).
 * 위 외곽 경계는 첫 행(targetRow=0)을 reverse=true로 조정한다.
 */
export function resizeRowHeights(
  rowHeights: (number | string)[] | undefined,
  rowCount: number,
  targetRow: number,
  deltaPx: number,
  measuredPx: number[],
  minPx: number = MIN_EDGE_PX,
  reverse: boolean = false,
): (number | string)[] {
  const norm = normalize(rowHeights, rowCount);
  const cur = norm[targetRow];
  const base = typeof cur === 'number' ? cur : (measuredPx[targetRow] ?? minPx);
  const effDelta = reverse ? -deltaPx : deltaPx;
  const next = Math.max(minPx, Math.round(base + effDelta));
  const out: (number | string)[] = norm.map((h) => (h === undefined ? 'auto' : h));
  out[targetRow] = next;
  return out;
}
